import { useEffect, useMemo, useState, useCallback } from 'react';
import {
  makeStyles,
  tokens,
  Text,
  Caption1,
  Body1,
  Button,
  Spinner,
  SearchBox,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  MessageBar,
  MessageBarBody,
  Tooltip,
  DataGrid,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridBody,
  DataGridRow,
  DataGridCell,
  createTableColumn,
  type TableColumnDefinition,
} from '@fluentui/react-components';
import {
  ArrowDownloadRegular,
  DeleteRegular,
  SearchRegular,
  DocumentBulletListRegular,
  ChevronLeftRegular,
  ChevronRightRegular,
  ArrowUpRegular,
  ArrowDownRegular,
} from '@fluentui/react-icons';
import { useNavigate } from 'react-router-dom';
import { useGraphConsent } from '@/features/discovery/hooks/useGraphConsent';
import {
  listReports,
  getStoredReportUrl,
  deleteReport,
} from '@/features/discovery/services/discoveryApi';
import {
  INDUSTRY_OPTIONS,
  USER_ROLE_OPTIONS,
  type StoredReport,
} from '@/features/discovery/types/discovery.types';

// Value → human label maps so the row title reads like the report subject.
const INDUSTRY_LABELS: Record<string, string> = Object.fromEntries(
  INDUSTRY_OPTIONS.map((o) => [o.value, o.label]),
);
const ROLE_LABELS: Record<string, string> = Object.fromEntries(
  USER_ROLE_OPTIONS.map((o) => [o.value, o.label]),
);
import { PageBanner } from '@/components/common/PageBanner';
import { transformrBrand, brandFonts } from '@/theme/transformrTheme';

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalXXL,
  },
  // Banner full-width; page content (like Book a Meeting) at 70%, left-aligned.
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalXXL,
    maxWidth: '70%',
    // 70% only above 1600px; at 1600px and below (incl. mobile) stretch to 100%.
    '@media (max-width: 1600px)': { maxWidth: '100%' },
  },
  // Bordered card wraps the toolbar + grid + footer (Ops Manager /dor-admin list).
  listCard: {
    display: 'flex',
    flexDirection: 'column',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    overflow: 'hidden',
  },
  // Toolbar holds the search box (left), matching the dor-admin projects page.
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    padding: tokens.spacingHorizontalL,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  search: { width: '24rem', maxWidth: '50%' },
  // Footer: result count (left) + pager (right).
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  pager: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },
  footerInfo: {
    fontFamily: brandFonts.body,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  noMatch: {
    padding: `calc(${tokens.spacingVerticalXXL} + ${tokens.spacingVerticalS})`,
    textAlign: 'center',
    color: tokens.colorNeutralForeground2,
  },
  // DataGrid styled to the Ops Manager /dor-admin list: Report column stretches
  // + wraps, Saved/Size/Actions fixed; header is Plus Jakarta uppercase tracked.
  grid: {
    // Column widths via flex-basis on each cell (header + body share the row).
    '& [role="row"] > :nth-child(1)': { flexGrow: 1, flexShrink: 1, flexBasis: 0, minWidth: 0 },
    '& [role="row"] > :nth-child(2)': { flexGrow: 0, flexShrink: 0, flexBasis: '11rem' },
    '& [role="row"] > :nth-child(3)': { flexGrow: 0, flexShrink: 0, flexBasis: '7rem' },
    '& [role="row"] > :nth-child(4)': { flexGrow: 0, flexShrink: 0, flexBasis: '6rem' },
    '& [role="columnheader"]': {
      fontFamily: brandFonts.heading,
      fontSize: tokens.fontSizeBase200,
      fontWeight: tokens.fontWeightSemibold,
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
      color: tokens.colorNeutralForeground1,
      // Match the Ops Manager header row height (12/16 cell padding); align
      // header text with the cell content below.
      paddingTop: tokens.spacingVerticalM,
      paddingBottom: tokens.spacingVerticalM,
      paddingLeft: tokens.spacingHorizontalL,
      paddingRight: tokens.spacingHorizontalL,
    },
    // Deliberate row spacing — roomy but trimmed vertically; padded horizontally.
    '& [role="gridcell"]': {
      alignItems: 'flex-start',
      paddingTop: tokens.spacingVerticalL,
      paddingBottom: tokens.spacingVerticalL,
      paddingLeft: tokens.spacingHorizontalL,
      paddingRight: tokens.spacingHorizontalL,
    },
    '& [role="gridcell"] > *': { minWidth: 0, whiteSpace: 'normal' },
  },
  rowActions: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
  },
  // Whole-header click target — inherits the uppercase tracked header styling.
  sortHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    width: '100%',
    cursor: 'pointer',
    userSelect: 'none',
    ':focus-visible': {
      outline: `2px solid ${tokens.colorBrandStroke1}`,
      outlineOffset: tokens.spacingHorizontalXXS,
    },
  },
  sortArrow: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  // Priority badge shown when more than one column is in the sort.
  sortRank: {
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground3,
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalS,
  },
  title: {
    fontSize: tokens.fontSizeHero700,
    fontWeight: tokens.fontWeightSemibold,
    margin: 0,
    color: tokens.colorNeutralForeground1,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingHorizontalL,
    padding: '48px 24px',
    textAlign: 'center',
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: `calc(${tokens.spacingVerticalXXXL} + ${tokens.spacingVerticalL})`,
  },
  // Row title — Plus Jakarta 16px semibold navy (dor-admin titleText). Wraps.
  reportName: {
    fontFamily: brandFonts.heading,
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '-0.01em',
    color: transformrBrand.navy,
  },
  // Date/meta cell — Inter, muted (dor-admin metaText).
  reportDate: {
    fontFamily: brandFonts.body,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  reportInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalSNudge,
  },
  reportInfoText: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    minWidth: 0,
  },
  // Row sub-line (description) — JetBrains Mono 10px grey (dor-admin idText). Wraps.
  reportSummary: {
    fontFamily: brandFonts.mono,
    fontSize: tokens.fontSizeBase100,
    lineHeight: tokens.lineHeightBase200,
    letterSpacing: '0.02em',
    color: transformrBrand.grey700,
  },
  infoIcon: {
    cursor: 'help',
    display: 'inline-flex',
    alignItems: 'center',
  },
});

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function displayName(fileName: string): string {
  return fileName.replace(/\.(pdf|docx)$/i, '');
}

export function ReportsPage() {
  const styles = useStyles();
  const navigate = useNavigate();
  const { acquireApiToken } = useGraphConsent();

  const [reports, setReports] = useState<StoredReport[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [apiToken, setApiToken] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  // Multi-column sort: an ordered stack of {column, direction}. Clicking a
  // header cycles that column asc → desc → off while keeping the others, so the
  // list can be sorted by several columns at once (e.g. report name + size).
  const [sortStack, setSortStack] = useState<Array<{ columnId: string; dir: 'ascending' | 'descending' }>>([
    { columnId: 'saved', dir: 'descending' },
  ]);

  const PAGE_SIZE = 12;

  const fetchReports = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = await acquireApiToken();
      if (!token) return;
      setApiToken(token);
      const data = await listReports(token);
      setReports(data.reports);
    } catch (err) {
      console.error('Failed to load reports:', err);
    } finally {
      setIsLoading(false);
    }
  }, [acquireApiToken]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  // Title reflects the report subject (industry · role · country), humanised —
  // not the company name (which is usually the user's own org and unhelpful).
  const reportTitle = useCallback((r: StoredReport) => {
    const parts = [
      r.industry ? INDUSTRY_LABELS[r.industry] ?? r.industry : '',
      r.userRole ? ROLE_LABELS[r.userRole] ?? r.userRole : '',
      r.country ?? '',
    ].filter(Boolean);
    return parts.length ? parts.join(' · ') : displayName(r.fileName);
  }, []);

  // Per-column comparators (single key each). The sort stack composes them in
  // priority order, so the user — not a fixed tie-breaker — drives the combo.
  const comparers = useMemo<Record<string, (a: StoredReport, b: StoredReport) => number>>(() => ({
    report: (a, b) => reportTitle(a).localeCompare(reportTitle(b), undefined, { sensitivity: 'base' }),
    saved: (a, b) => (a.storedAt ?? '').localeCompare(b.storedAt ?? ''),
    size: (a, b) => (a.sizeBytes || 0) - (b.sizeBytes || 0),
  }), [reportTitle]);

  // Cycle a column through the sort stack: absent → asc → desc → removed.
  const toggleSort = useCallback((columnId: string) => {
    setSortStack((prev) => {
      const idx = prev.findIndex((s) => s.columnId === columnId);
      if (idx === -1) return [...prev, { columnId, dir: 'ascending' as const }];
      const next = [...prev];
      if (prev[idx].dir === 'ascending') {
        next[idx] = { columnId, dir: 'descending' };
        return next;
      }
      next.splice(idx, 1); // third click drops it from the sort
      return next;
    });
    setPage(0);
  }, []);

  // Search by file name + all recall metadata / the description shown in the list.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return reports;
    return reports.filter((r) => {
      const haystack = [
        reportTitle(r), r.fileName, r.summary, r.aiGoals,
        r.industry, r.userRole, r.country, r.companyName,
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [reports, search, reportTitle]);

  // Sort the full filtered list (applying the whole stack) before paging, so
  // each page is globally correct and reflects every active sort column.
  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      for (const s of sortStack) {
        const cmp = comparers[s.columnId];
        if (!cmp) continue;
        const r = cmp(a, b) * (s.dir === 'descending' ? -1 : 1);
        if (r !== 0) return r;
      }
      return comparers.report(a, b); // stable final tie-break by name
    });
  }, [filtered, sortStack, comparers]);

  const pageCount = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const paged = useMemo(
    () => sorted.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE),
    [sorted, page],
  );

  // Keep the page in range when the result set shrinks (search/delete).
  useEffect(() => {
    if (page > pageCount - 1) setPage(pageCount - 1);
  }, [page, pageCount]);

  const handleDownload = async (analysisId: string) => {
    try {
      const token = apiToken ?? await acquireApiToken();
      if (!token) return;
      const data = await getStoredReportUrl(analysisId, token);
      window.open(data.downloadUrl, '_blank');
    } catch (err) {
      console.error('Failed to get download URL:', err);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      const token = apiToken ?? await acquireApiToken();
      if (!token) return;
      await deleteReport(deleteTarget, token);
      setReports((prev) => prev.filter((r) => r.analysisId !== deleteTarget));
      setDeleteTarget(null);
    } catch (err) {
      console.error('Failed to delete report:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  // Clickable, sortable header — whole cell toggles the column's sort; shows an
  // up/down arrow for direction and a priority number when >1 column is active.
  const sortHeader = (columnId: string, label: string) => {
    const idx = sortStack.findIndex((s) => s.columnId === columnId);
    const entry = idx >= 0 ? sortStack[idx] : null;
    return (
      <span
        className={styles.sortHeader}
        role="button"
        tabIndex={0}
        aria-label={`Sort by ${label}`}
        onClick={() => toggleSort(columnId)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSort(columnId); }
        }}
      >
        {label}
        {entry && (entry.dir === 'ascending'
          ? <ArrowUpRegular className={styles.sortArrow} />
          : <ArrowDownRegular className={styles.sortArrow} />)}
        {entry && sortStack.length > 1 && <span className={styles.sortRank}>{idx + 1}</span>}
      </span>
    );
  };

  // DataGrid columns — Report / Saved / Size headers drive the multi-column sort
  // stack (sorting + paging are handled by the page, not the grid).
  const columns: TableColumnDefinition<StoredReport>[] = [
    createTableColumn<StoredReport>({
      columnId: 'report',
      renderHeaderCell: () => sortHeader('report', 'Report'),
      renderCell: (r) => (
        <div className={styles.reportInfoText}>
          <Text className={styles.reportName}>{reportTitle(r)}</Text>
          {(r.summary || r.aiGoals) && (
            <Caption1 className={styles.reportSummary}>{r.summary || r.aiGoals}</Caption1>
          )}
        </div>
      ),
    }),
    createTableColumn<StoredReport>({
      columnId: 'saved',
      renderHeaderCell: () => sortHeader('saved', 'Saved'),
      renderCell: (r) => <Caption1 className={styles.reportDate}>{formatDate(r.storedAt)}</Caption1>,
    }),
    createTableColumn<StoredReport>({
      columnId: 'size',
      renderHeaderCell: () => sortHeader('size', 'Size'),
      renderCell: (r) => <Caption1 className={styles.reportDate}>{formatBytes(r.sizeBytes)}</Caption1>,
    }),
    createTableColumn<StoredReport>({
      columnId: 'actions',
      renderHeaderCell: () => 'Actions',
      renderCell: (r) => (
        <div className={styles.rowActions}>
          <Tooltip content="Download" relationship="label">
            <Button appearance="subtle" size="small" icon={<ArrowDownloadRegular />}
              aria-label="Download" onClick={() => handleDownload(r.analysisId)} />
          </Tooltip>
          <Tooltip content="Delete" relationship="label">
            <Button appearance="subtle" size="small" icon={<DeleteRegular />}
              aria-label="Delete" onClick={() => setDeleteTarget(r.analysisId)} />
          </Tooltip>
        </div>
      ),
    }),
  ];

  if (isLoading) {
    return (
      <div className={styles.page}>
        <PageBanner
          kicker="Your reports"
          title="My Reports"
          subtitle="Previously saved Readiness reports for your tenant."
          icon={<DocumentBulletListRegular fontSize={14} />}
        />
        <div className={styles.loading}>
          <Spinner size="large" label="Loading reports..." />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <PageBanner
        kicker="Your reports"
        title="My Reports"
        subtitle="Previously saved Readiness reports for your tenant."
        icon={<DocumentBulletListRegular fontSize={14} />}
      />

      <div className={styles.content}>
      {reports.length > 0 && (
        <MessageBar intent="info">
          <MessageBarBody>
            Reports are kept for 6 months, then automatically deleted. Download a copy to keep it permanently.
          </MessageBarBody>
        </MessageBar>
      )}

      {reports.length === 0 ? (
        <div className={styles.emptyState}>
          <SearchRegular fontSize={48} color={tokens.colorNeutralForeground3} />
          <Text size={500} weight="semibold">
            No saved reports yet
          </Text>
          <Body1 style={{ color: tokens.colorNeutralForeground2 }}>
            Run a Readiness assessment and save the report to see it here.
          </Body1>
          <Button
            appearance="primary"
            onClick={() => navigate('/readiness')}
          >
            Start Assessment
          </Button>
        </div>
      ) : (
        <div className={styles.listCard}>
          <div className={styles.toolbar}>
            <SearchBox
              appearance="underline"
              className={styles.search}
              placeholder="Search by name or description…"
              value={search}
              onChange={(_e, d) => { setSearch(d.value); setPage(0); }}
              aria-label="Search reports"
            />
          </div>

          {sorted.length === 0 ? (
            <Body1 className={styles.noMatch}>No reports match your search.</Body1>
          ) : (
            <DataGrid
              items={paged}
              columns={columns}
              getRowId={(r) => r.analysisId}
              className={styles.grid}
            >
              <DataGridHeader>
                <DataGridRow>
                  {({ renderHeaderCell }) => <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>}
                </DataGridRow>
              </DataGridHeader>
              <DataGridBody<StoredReport>>
                {({ item, rowId }) => (
                  <DataGridRow<StoredReport> key={rowId}>
                    {({ renderCell }) => <DataGridCell>{renderCell(item)}</DataGridCell>}
                  </DataGridRow>
                )}
              </DataGridBody>
            </DataGrid>
          )}

          {sorted.length > 0 && (
            <div className={styles.footer}>
              <span className={styles.footerInfo}>
                {`Showing ${page * PAGE_SIZE + 1}–${Math.min(sorted.length, (page + 1) * PAGE_SIZE)} of ${sorted.length} report${sorted.length === 1 ? '' : 's'}`}
              </span>
              {pageCount > 1 && (
                <div className={styles.pager}>
                  <Button
                    appearance="subtle"
                    size="small"
                    icon={<ChevronLeftRegular />}
                    aria-label="Previous page"
                    disabled={page === 0}
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                  />
                  <span className={styles.footerInfo}>{`Page ${page + 1} of ${pageCount}`}</span>
                  <Button
                    appearance="subtle"
                    size="small"
                    icon={<ChevronRightRegular />}
                    aria-label="Next page"
                    disabled={page >= pageCount - 1}
                    onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      )}
      </div>

      <Dialog
        open={deleteTarget !== null}
        onOpenChange={(_, data) => { if (!data.open) setDeleteTarget(null); }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Delete Report</DialogTitle>
            <DialogContent>
              Are you sure you want to delete this report? This action cannot be undone.
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary" disabled={isDeleting}>Cancel</Button>
              </DialogTrigger>
              <Button
                appearance="primary"
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
